import { useEffect, useState } from "react";
import { api, idr, idrShort } from "../../lib/api";
import { toast } from "sonner";
import { PiggyBank, Plus, Camera, RefreshCw, Check, X, Settings, AlertTriangle, Send, FileSpreadsheet, Eye, ChevronRight, Smartphone, Wallet } from "lucide-react";
import InputRupiah from "../../components/InputRupiah";
import { DatePicker } from "../../components/ui/date-picker";
import { hariIniISO, bulanIniISO, formatTanggal } from "../../lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useAuth, isPermissionActionExpired } from "../../context/AuthContext";
import { useCabangOptions } from "../../lib/cabang";
import { BatasDaftarNotice } from "./_shared";

// Cek izin EKSPLISIT (dicentang lewat User Management) — TIDAK jatuh ke
// default role generik seperti `can()` biasa. KENAPA perlu versi sesempit
// ini: backend (`izin_eksplisit_finance` di `routers/finance/_shared.py`)
// sengaja membatasi approve/reject/hapus petty cash lebih sempit daripada
// role default finance_* (yang penuh untuk SPV/CEO tapi backend hanya
// meloloskan role tertentu + izin eksplisit). Kalau tombolnya digerbangi
// `can()` biasa di sini, finance_staff akan MELIHAT tombol Approve padahal
// backend selalu menolaknya — melanggar CLAUDE.md #3 (jangan tampilkan
// tombol yang ujungnya 403).
function izinEksplisit(user, module, action) {
  if (!user) return false;
  const perms = user.permissions || {};
  const row = perms[module];
  if (row && typeof row === "object" && Object.prototype.hasOwnProperty.call(row, action)) {
    return !!row[action] && !isPermissionActionExpired(row, action);
  }
  if (typeof module === "string" && module.startsWith("finance_")) {
    const legacy = perms.finance;
    if (legacy && typeof legacy === "object" && Object.prototype.hasOwnProperty.call(legacy, action)) {
      return !!legacy[action] && !isPermissionActionExpired(legacy, action);
    }
  }
  return false;
}
// Role lama yang tetap diistimewakan apa adanya — SAMA PERSIS dengan daftar
// role hardcode di `backend/routers/finance/petty_cash.py` (aditif: role
// lama tetap bisa, izin eksplisit menambah, tidak mengurangi).
const bolehHapusPetty = (user) =>
  ["finance_ceo", "admin", "superadmin"].includes(user?.role) || izinEksplisit(user, "finance_petty", "delete");
const bolehApproveRejectPetty = (user) =>
  ["finance_spv", "finance_ceo", "admin", "superadmin"].includes(user?.role) || izinEksplisit(user, "finance_petty", "update");

export default function FinancePettyCash() {
  const [tab, setTab] = useState("overview");
  // G3/G4 (brief owner 15 Sep 2026). Owner melaporkan dropdown kas kecil "tidak
  // dapat ditekan" dan menduga itu soal izin. Bukan: SEMUA akun kas kecilnya
  // `jenis`-nya belum disetel, jadi setiap `<option>` lahir `disabled`.
  // Penjelasannya sudah ada di layar — TAPI hanya muncul setelah sebuah akun
  // TERPILIH, dan justru itu yang mustahil dilakukan. Jadi remedinya harus bisa
  // dicapai dari tempat pengguna terjebak, bukan dari tempat yang baru
  // terbuka setelah masalahnya selesai.
  const [settingAkunId, setSettingAkunId] = useState(null);
  const bukaSetting = (id) => { setSettingAkunId(id); setTab("overview"); };
  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-petty-cash">
      <div>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Petty Cash · Imprest System</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Kas Kecil</h1>
        <p className="text-sm text-slate-600 mt-1">Saldo tetap · replenishment approval SPV · foto nota wajib</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-1 inline-flex gap-1">
        {[
          { k: "overview", l: "Overview", i: PiggyBank },
          { k: "expense",  l: "Pengeluaran", i: Camera },
          { k: "replenish", l: "Replenishment", i: RefreshCw },
        ].map(t => {
          const Icon = t.i;
          return <button key={t.k} onClick={() => setTab(t.k)} data-testid={`petty-tab-${t.k}`} className={`inline-flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-medium ${tab === t.k ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-slate-50"}`}><Icon className="w-4 h-4" /> {t.l}</button>;
        })}
      </div>

      {tab === "overview" && (
        <TabOverview settingAkunId={settingAkunId} onSettingDibuka={() => setSettingAkunId(null)} />
      )}
      {tab === "expense" && <TabExpense bukaSetting={bukaSetting} />}
      {tab === "replenish" && <TabReplenish bukaSetting={bukaSetting} />}
    </div>
  );
}

function TabOverview({ settingAkunId, onSettingDibuka }) {
  const { user, can } = useAuth();
  const [accounts, setAccounts] = useState([]);
  // K7 (brief 4 Sep 2026): total SEMUA kas kecil aktif dihitung server dari
  // seluruh akun — bukan dari `accounts` yang sudah disaring cabang/divisi.
  const [ringkas, setRingkas] = useState({ total_saldo_aktif: 0, jumlah_akun_aktif: 0, min_balance_bawaan: 0 });
  const [showSet, setShowSet] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [kasBank, setKasBank] = useState([]);
  const [filterCabang, setFilterCabang] = useState("");
  const [filterDivisi, setFilterDivisi] = useState("");
  const [divisiOptions, setDivisiOptions] = useState([]);
  // K5: kartu yang sedang dibuka riwayatnya. Panel menggantikan grid kartu —
  // pola yang SAMA dengan `FinanceKasBank.jsx::MutasiPanel`, supaya panel tidak
  // pernah muncul di luar layar setelah pengguna menggulir ke kartu paling bawah.
  const [akunRiwayat, setAkunRiwayat] = useState(null);
  const cabangOptions = useCabangOptions();
  const load = (cabang = filterCabang, divisi = filterDivisi) => {
    const params = {};
    if (cabang) params.cabang = cabang;
    if (divisi) params.divisi = divisi;
    return api.get("/finance/petty-cash/accounts", { params }).then(r => { setAccounts(r.data.items || []); setRingkas(r.data || {}); });
  };
  useEffect(() => {
    load();
    api.get("/finance/accounts/kas-bank").then(r => setKasBank(r.data || []));
    // Daftar divisi TERBUKA untuk semua akun (GET /divisions), tapi bisa kosong
    // di produksi — dropdown filter/form harus mengatakan itu apa adanya,
    // bukan tampil kosong tanpa penjelasan (permintaan bos 21 Agu 2026).
    api.get("/divisions").then(r => setDivisiOptions((r.data || []).map(d => d.nama).filter(Boolean)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Permintaan "setel jenis kas akun X" datang dari tab lain (lihat
  // `PanduanAkunBelumSiap`). Dijalankan SETELAH `accounts` termuat — kalau
  // tidak, `find` gagal dan permintaannya hilang tanpa satu pesan pun.
  useEffect(() => {
    if (!settingAkunId || accounts.length === 0) return;
    const akun = accounts.find(a => a.id === settingAkunId);
    if (akun) setShowSet(akun);
    // Dibersihkan apa pun hasilnya: kalau akunnya sudah tidak ada (dihapus /
    // tersaring cabang), permintaan yang menggantung akan membuka modal yang
    // salah begitu daftarnya berubah.
    onSettingDibuka();
  }, [settingAkunId, accounts, onSettingDibuka]);

  const del = async (a) => {
    if (!window.confirm(`Hapus petty cash '${a.name}'? (Saldo harus 0)`)) return;
    try { const r = await api.delete(`/finance/petty-cash/accounts/${a.id}`); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-slate-600">{accounts.length} petty cash aktif</p>
          <p className="font-display text-lg font-bold text-slate-900" data-testid="petty-total-saldo">
            Total saldo semua kas kecil: {idr(ringkas.total_saldo_aktif || 0)}
            <span className="ml-2 text-xs font-normal text-slate-500">({ringkas.jumlah_akun_aktif || 0} akun aktif, semua cabang)</span>
            {ringkas.saldo_kliring ? (
              <span className="block text-xs font-normal text-amber-700" data-testid="petty-total-kliring">
                termasuk akun kliring trade-in 1055: {idr(ringkas.saldo_kliring)} — bukan uang di laci
              </span>
            ) : null}
          </p>
        </div>
        {can("finance_petty", "create") && (
          <button onClick={() => setShowNew(true)} data-testid="petty-new-account" className="h-10 px-4 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Petty Cash Baru
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <select value={filterCabang} onChange={e => { setFilterCabang(e.target.value); load(e.target.value, filterDivisi); }} className="in" style={{ width: 200 }} data-testid="petty-filter-cabang">
          <option value="">Semua cabang</option>
          {cabangOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterDivisi} onChange={e => { setFilterDivisi(e.target.value); load(filterCabang, e.target.value); }} className="in" style={{ width: 200 }} data-testid="petty-filter-divisi">
          <option value="">Semua divisi</option>
          {divisiOptions.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      {/* K8: gerbangnya `finance_petty.view` — sama dengan endpoint export-nya,
          jadi tombol ini tidak pernah berujung 403 (aturan #3). */}
      <ExportKasKecil cabangOptions={cabangOptions} />
      {akunRiwayat ? (
        <PanelRiwayatKasKecil akun={akunRiwayat} onClose={() => setAkunRiwayat(null)} />
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map(a => (
          // Kartu memuat tombol Setting/Hapus di dalamnya, jadi ia TIDAK boleh
          // jadi <button> (tombol bersarang = DOM tidak sah, dan browser boleh
          // membongkarnya sesuka hati). `role="button"` + `tabIndex` + handler
          // keyboard memberi perilaku yang sama untuk pengguna keyboard.
          <div key={a.id} role="button" tabIndex={0}
            aria-label={`Buka riwayat transaksi ${a.name}`}
            onClick={() => setAkunRiwayat(a)}
            onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAkunRiwayat(a); } }}
            data-testid={`petty-akun-${a.code}`}
            className={`text-left cursor-pointer bg-white border-2 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all ${a.needs_replenish ? "border-rose-300" : "border-slate-200"}`}>
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg grid place-items-center bg-purple-50 text-purple-600`}><PiggyBank className="w-5 h-5" /></div>
              <div className="flex items-center gap-1">
                {can("finance_petty", "update") && (
                  <button onClick={e => { e.stopPropagation(); setShowSet(a); }} data-testid={`petty-set-${a.code}`} title="Setting" className="text-slate-400 hover:text-slate-700"><Settings className="w-4 h-4" /></button>
                )}
                {bolehHapusPetty(user) && (
                  <button onClick={e => { e.stopPropagation(); del(a); }} data-testid={`petty-del-${a.code}`} title="Hapus" className="text-slate-400 hover:text-rose-700"><X className="w-4 h-4" /></button>
                )}
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </div>
            </div>
            <p className="text-[10px] text-slate-400 font-mono">{a.code}</p>
            <p className="text-sm text-slate-600">{a.name}</p>
            <BadgeJenisKas akun={a} />
            <p className="font-display text-2xl font-bold text-slate-900 mt-1">{idr(a.balance)}</p>
            <div className="mt-3 text-xs space-y-1 border-t border-slate-100 pt-2">
              <p className="text-slate-500">Imprest: <b className="text-slate-700">{idr(a.imprest_amount || 0)}</b></p>
              <p className="text-slate-500">Min Saldo: <b className="text-slate-700">{idr(a.min_balance_efektif ?? a.min_balance ?? 0)}</b>
                {!(a.min_balance > 0) && (a.min_balance_efektif > 0) && <span className="ml-1 text-[10px] text-slate-400" title="Akun ini belum menyetel ambang sendiri — memakai ambang bawaan dari Master > Konfigurasi">(bawaan)</span>}
              </p>
              <p className="text-slate-500">Cabang: <b className="text-slate-700">{a.cabang || "- Umum -"}</b></p>
              <p className="text-slate-500">Divisi: <b className="text-slate-700">{a.divisi || "- Umum -"}</b></p>
              {a.gap_replenish > 0 && <p className="text-purple-700">Gap replenish: <b>{idr(a.gap_replenish)}</b></p>}
            </div>
            {a.needs_replenish && <div className="mt-3 flex items-center gap-1.5 text-rose-700 text-xs font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> Saldo di bawah minimum!</div>}
            <p className="mt-3 text-[11px] text-purple-700 font-medium">Klik kartu untuk melihat riwayat transaksinya →</p>
          </div>
        ))}
      </div>
      )}
      {showSet && <FormSet acc={showSet} kasBank={kasBank} cabangOptions={cabangOptions} divisiOptions={divisiOptions} onClose={() => { setShowSet(null); load(); }} />}
      {showNew && <FormNewPetty kasBank={kasBank} cabangOptions={cabangOptions} divisiOptions={divisiOptions} onClose={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function CabangDivisiFields({ f, setF, cabangOptions, divisiOptions }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <Fld label="Cabang (opsional)">
        <select value={f.cabang} onChange={e => setF({ ...f, cabang: e.target.value })} className="in" data-testid="petty-cabang">
          <option value="">- Tidak set -</option>
          {cabangOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Fld>
      <Fld label="Divisi (opsional)">
        {divisiOptions.length === 0 ? (
          <p className="text-[11px] text-slate-400 italic pt-2">Belum ada divisi terdaftar — buat dulu di Master Data &gt; Divisi.</p>
        ) : (
          <select value={f.divisi} onChange={e => setF({ ...f, divisi: e.target.value })} className="in" data-testid="petty-divisi">
            <option value="">- Tidak set -</option>
            {divisiOptions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
      </Fld>
    </div>
  );
}

// K3 (brief 4 Sep 2026): jenis kas menentukan KODE METODE nomor bukti — tunai
// dapat PCT/PIT, e-wallet dapat PCE/PIE. Wajib untuk akun BARU: akun tanpa jenis
// ditolak backend saat nomor diminta (nomor tidak pernah ditebak), jadi
// membiarkannya kosong sama saja membuat akun yang belum bisa dipakai.
const JENIS_KAS_OPSI = [
  { v: "tunai", l: "Tunai (uang fisik di laci)" },
  { v: "ewallet", l: "E-Wallet (GoPay/OVO/DANA/ShopeePay dll)" },
];

function JenisKasField({ nilai, onChange, bolehKosong }) {
  return (
    <Fld label="Jenis Kas (wajib)">
      <select value={nilai || ""} onChange={e => onChange(e.target.value)} className="in" data-testid="petty-jenis">
        {bolehKosong && <option value="">- Belum disetel -</option>}
        {!bolehKosong && <option value="">Pilih jenis...</option>}
        {JENIS_KAS_OPSI.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
      <p className="text-[11px] text-slate-500 mt-1">Menentukan kode nomor bukti: Tunai → PCT/PIT, E-Wallet → PCE/PIE. Belum disetel = belum bisa mencatat pengeluaran.</p>
    </Fld>
  );
}

function FormNewPetty({ kasBank, cabangOptions, divisiOptions, onClose }) {
  const [f, setF] = useState({ name: "", code: "", imprest_amount: 500000, min_balance: 100000, cabang: "", divisi: "", jenis: "", replenishment_source_akun_id: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Nama wajib diisi");
    if (!f.jenis) return toast.error("Jenis kas (Tunai/E-Wallet) wajib dipilih — nomor bukti tidak bisa ditebak dari nama akun");
    setBusy(true);
    try {
      const payload = {
        name: f.name.trim(),
        code: f.code.trim() || null,
        imprest_amount: Number(f.imprest_amount || 0),
        min_balance: Number(f.min_balance || 0),
        cabang: f.cabang || null,
        divisi: f.divisi || null,
        jenis: f.jenis,
        replenishment_source_akun_id: f.replenishment_source_akun_id || null,
      };
      const r = await api.post("/finance/petty-cash/accounts", payload);
      toast.success(r.data.message); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  // `dangerous` — form panjang (7 field) untuk akun petty cash baru; kehilangannya
  // lewat klik/Escape tak sengaja berarti mengetik ulang semua.
  const guard = useModalGuard({ onClose, onSubmit: submit, busy, dangerous: true });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid="petty-new-modal" className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-1">Petty Cash Baru</h3>
        <p className="text-xs text-slate-500 mb-4">Tambah akun petty cash (kas kecil) baru. Kode auto-generate 105X kalau dikosongkan.</p>
        <div className="space-y-3">
          <Fld label="Nama Petty Cash"><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Contoh: Petty Cash Cabang Bandung" data-testid="petty-new-name" className="in" /></Fld>
          <Fld label="Kode (opsional)"><input value={f.code} onChange={e => setF({ ...f, code: e.target.value })} placeholder="1051 (auto)" data-testid="petty-new-code" className="in" /></Fld>
          <JenisKasField nilai={f.jenis} onChange={v => setF({ ...f, jenis: v })} />
          <CabangDivisiFields f={f} setF={setF} cabangOptions={cabangOptions} divisiOptions={divisiOptions} />
          <div className="grid grid-cols-2 gap-2">
            <Fld label="Imprest Amount"><InputRupiah value={f.imprest_amount} onChange={n => setF({ ...f, imprest_amount: n })} testid="petty-new-imp" className="in" /></Fld>
            <Fld label="Min Balance"><InputRupiah value={f.min_balance} onChange={n => setF({ ...f, min_balance: n })} className="in" /></Fld>
          </div>
          <Fld label="Sumber Auto-Replenish (opsional)"><select value={f.replenishment_source_akun_id} onChange={e => setF({ ...f, replenishment_source_akun_id: e.target.value })} className="in"><option value="">- Tidak set -</option>{kasBank.filter(a => a.category === "bank").map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></Fld>
        </div>
        {guard.confirming && (
          <div className="mt-3 -mx-6"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="petty-new-guard" /></div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={busy || !f.jenis || !f.name.trim()} data-testid="petty-new-submit" className="flex-1 h-10 rounded-lg bg-purple-600 text-white hover:bg-purple-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Simpan"}</button>
        </div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #9333EA; }`}</style>
      </div>
    </div>
  );
}

function FormSet({ acc, kasBank, cabangOptions, divisiOptions, onClose }) {
  const [f, setF] = useState({ imprest_amount: acc.imprest_amount || 0, min_balance: acc.min_balance || 0, cabang: acc.cabang || "", divisi: acc.divisi || "", jenis: acc.jenis || "", replenishment_source_akun_id: acc.replenishment_source_akun_id || "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    // `cabang`/`divisi`/`jenis` SELALU dikirim (walau "" -> null) — endpoint PUT
    // hanya menyentuh field yang benar2 ada di body (lihat `model_fields_set` di
    // backend), jadi form ini harus selalu mengirimnya supaya perubahan
    // (termasuk mengosongkan) benar-benar tersimpan.
    try { const r = await api.put(`/finance/petty-cash/accounts/${acc.id}`, { imprest_amount: Number(f.imprest_amount), min_balance: Number(f.min_balance), cabang: f.cabang || null, divisi: f.divisi || null, jenis: f.jenis || null, replenishment_source_akun_id: f.replenishment_source_akun_id || null }); toast.success(r.data.message); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-1">Setting {acc.name}</h3>
        <p className="text-xs text-slate-500 mb-4">Imprest = saldo tetap · Min = ambang alert Telegram</p>
        {acc.kode_bukti_siap === false && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="petty-set-kode-masalah">
            <b>Akun ini belum bisa mencatat pengeluaran.</b> {acc.kode_bukti_masalah}
          </div>
        )}
        <div className="space-y-3">
          <JenisKasField nilai={f.jenis} onChange={v => setF({ ...f, jenis: v })} bolehKosong />
          <CabangDivisiFields f={f} setF={setF} cabangOptions={cabangOptions} divisiOptions={divisiOptions} />
          <Fld label="Imprest Amount (Saldo Tetap)"><InputRupiah value={f.imprest_amount} onChange={n => setF({ ...f, imprest_amount: n })} className="in" testid="petty-imprest" /></Fld>
          <Fld label="Min Balance (Alert)"><InputRupiah value={f.min_balance} onChange={n => setF({ ...f, min_balance: n })} className="in" testid="petty-min" /></Fld>
          <Fld label="Bank Sumber Auto-Replenish (opsional)"><select value={f.replenishment_source_akun_id} onChange={e => setF({ ...f, replenishment_source_akun_id: e.target.value })} className="in"><option value="">- Tidak set -</option>{kasBank.filter(a => a.category === "bank").map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></Fld>
        </div>
        <div className="flex gap-2 mt-5"><button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Batal</button><button onClick={submit} disabled={busy} data-testid="petty-set-save" className="flex-1 h-10 rounded-lg bg-purple-600 text-white hover:bg-purple-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Simpan"}</button></div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #9333EA; }`}</style>
      </div>
    </div>
  );
}
function Fld({ label, children, col2 }) { return <div className={col2 ? "col-span-2" : ""}><label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>{children}</div>; }

// K4: hanya empat tipe dari brief, dan batas 20 MB. Dicek juga di server
// (termasuk MAGIC BYTES); pemeriksaan di sini semata supaya berkas 40 MB tidak
// diunggah dulu baru ditolak — bukan sebagai gerbang keamanan.
const EKSTENSI_BUKTI = [".jpg", ".jpeg", ".png", ".pdf"];
const MAKS_BUKTI_BYTE = 20 * 1024 * 1024;

/** Unggah bukti kas kecil — SATU sumber untuk pengeluaran (K4), pengajuan
 *  top-up, dan persetujuan top-up (14c). Dulu logikanya cuma ada di form
 *  pengeluaran; menyalinnya ke dua tempat baru adalah cara paling cepat membuat
 *  ketiganya berpisah jalan (jebakan duplikasi helper di CLAUDE.md). */
function useUnggahBukti() {
  const [bukti, setBukti] = useState(null);
  const [unggah, setUnggah] = useState(false);
  const pilihBerkas = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Direset lebih dulu supaya memilih berkas yang SAMA lagi (setelah gagal)
    // tetap memicu onChange.
    e.target.value = "";
    if (!file) return;
    const titik = file.name.lastIndexOf(".");
    const ext = titik >= 0 ? file.name.slice(titik).toLowerCase() : "";
    if (!EKSTENSI_BUKTI.includes(ext)) { toast.error("Bukti harus berkas JPG/JPEG/PNG/PDF"); return; }
    if (file.size > MAKS_BUKTI_BYTE) { toast.error("Berkas terlalu besar. Maksimal 20 MB"); return; }
    setUnggah(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/finance/petty-cash/bukti", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBukti(r.data.bukti);
      toast.success(r.data.message);
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal mengunggah bukti"); }
    finally { setUnggah(false); }
  };
  return { bukti, setBukti, unggah, pilihBerkas };
}

function PemilihBukti({ h, testid }) {
  return (
    <>
      <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={h.pilihBerkas} disabled={h.unggah}
             data-testid={testid}
             className="w-full text-sm text-slate-600 file:mr-3 file:h-9 file:px-3 file:rounded-lg file:border-0 file:bg-purple-50 file:text-purple-700 file:text-sm file:font-medium hover:file:bg-purple-100 disabled:opacity-50" />
      {h.unggah && <p className="text-[11px] text-slate-500 mt-1">Mengunggah...</p>}
      {h.bukti && (
        <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1" data-testid={`${testid}-nama`}>
          <Check className="w-3.5 h-3.5" /> {h.bukti.original_name} terunggah
          <button type="button" onClick={() => h.setBukti(null)} className="ml-1 underline text-slate-500 hover:text-slate-800">ganti</button>
        </p>
      )}
    </>
  );
}

function TabExpense({ bukaSetting }) {
  const { can } = useAuth();
  const [petty, setPetty] = useState([]);
  const cabangOptions = useCabangOptions();
  // K2: nama karyawan sebagai SARAN (datalist) — tetap boleh mengetik nama lain
  // (PIC bisa bukan karyawan, mis. kurir). Gagal memuat (tanpa izin HR) = tanpa
  // saran, bukan tanpa form.
  const [karyawan, setKaryawan] = useState([]);
  const [bebanAccs, setBebanAccs] = useState([]);
  const [f, setF] = useState({ tanggal: hariIniISO(), akun_id: "", kategori_beban_id: "", nominal: "", penerima: "", keterangan: "", cabang: "" });
  const [busy, setBusy] = useState(false);
  // K4: bukti diunggah LEBIH DULU (jadi berkas ber-id), lalu id-nya dikirim
  // bersama pengeluaran — backend mengklaimnya secara atomik. Input URL lama
  // dihapus: staf cabang tidak punya tempat menaruh foto selain menu Documents,
  // dan URL Documents ber-JWT yang mereka tempel bisa kedaluwarsa diam-diam.
  const hBukti = useUnggahBukti();
  // `unggah` ikut diambil: tombol "Catat Pengeluaran" memakainya untuk mengunci
  // diri selagi berkas bukti masih naik. Saat form ini dipindah ke hook
  // (6 Sep 2026) variabelnya sempat tidak ikut terbawa — dan `no-undef` belum
  // aktif waktu itu, jadi build tetap hijau.
  const { bukti, setBukti, unggah } = hBukti;
  // Cabang yang belum punya kode nomor bukti — ditandai di dropdown supaya
  // pilihannya tidak berujung 400 setelah semua kolom terisi (CLAUDE.md #3).
  const [tanpaKode, setTanpaKode] = useState([]);
  const [pratinjau, setPratinjau] = useState(null);
  useEffect(() => {
    api.get("/finance/petty-cash/accounts").then(r => setPetty(r.data.items || []));
    api.get("/finance/coa?category=beban").then(r => setBebanAccs((r.data || []).filter(a => a.category === "beban")));
    // Registry kode boleh dibaca pemegang finance_petty.view ATAU
    // finance_pengaturan.view; kalau gagal, dropdown cabang cuma kehilangan
    // penandanya — formnya tetap jalan dan backend tetap menjaga.
    api.get("/finance/petty-cash/kode-bukti").then(r => setTanpaKode(r.data.tanpa_kode || [])).catch(() => setTanpaKode([]));
    api.get("/employees").then(r => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      // Nama bisa kembar (dua "Budi") — datalist butuh key unik, dan satu
      // saran per nama sudah cukup.
      setKaryawan([...new Set(rows.map(k => k.nama).filter(Boolean))]);
    }).catch(() => setKaryawan([]));
  }, []);

  // Nomor berikutnya diambil dari BACKEND (bukan dirangkai di sini): angka
  // urutnya hidup di counter server, dan pratinjau yang dikarang klien selalu
  // menunjukkan 0001 — bug yang sudah pernah diperbaiki sekali (brief 17 Agu).
  // Ditunda 350 ms supaya mengganti-ganti dropdown tidak membanjiri server.
  useEffect(() => {
    if (!f.akun_id) { setPratinjau(null); return undefined; }
    let batal = false;
    const timer = setTimeout(() => {
      api.get("/finance/petty-cash/nomor-bukti/pratinjau", { params: { akun_id: f.akun_id, cabang: f.cabang || undefined, arah: "keluar" } })
        .then(r => { if (!batal) setPratinjau({ nomor: r.data.nomor }); })
        .catch(e => { if (!batal) setPratinjau({ error: e?.response?.data?.detail || "Nomor bukti belum bisa dihitung untuk pilihan ini." }); });
    }, 350);
    return () => { batal = true; clearTimeout(timer); };
  }, [f.akun_id, f.cabang]);

  const akunTerpilih = petty.find(a => a.id === f.akun_id);
  const cabangBelumKode = !!f.cabang && tanpaKode.includes(f.cabang);
  const belumSiap = (akunTerpilih && akunTerpilih.kode_bukti_siap === false) || cabangBelumKode;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/finance/petty-cash/expense", { ...f, nominal: Number(f.nominal), cabang: f.cabang || null, bukti_file_id: bukti ? bukti.id : null });
      toast.success(`${r.data.message} · ${r.data.code}`);
      setF({ ...f, nominal: "", keterangan: "", penerima: "" });
      // Bukti dilepas dari form: satu berkas hanya boleh dipakai SATU
      // pengeluaran (diklaim atomik di backend), jadi menyisakannya di layar
      // akan membuat submit berikutnya ditolak "sudah dipakai".
      setBukti(null);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 max-w-xl">
      <h3 className="font-display font-bold text-lg mb-4">Catat Pengeluaran Petty Cash</h3>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Tanggal"><DatePicker value={f.tanggal} onChange={v => setF({ ...f, tanggal: v })} data-testid="petty-exp-tanggal" /></Fld>
        <Fld label="Nominal"><InputRupiah value={f.nominal} onChange={n => setF({ ...f, nominal: n })} className="in" testid="petty-exp-nominal" /></Fld>
        <div className="col-span-2">
          <PanduanAkunBelumSiap petty={petty} bukaSetting={bukaSetting} testid="petty-exp-panduan" />
        </div>
        <Fld label="Dari Petty Cash" col2>
          <select value={f.akun_id} onChange={e => setF({ ...f, akun_id: e.target.value })} className="in" data-testid="petty-exp-akun">
            <option value="">Pilih...</option>
            {petty.map(a => (
              <option key={a.id} value={a.id} disabled={a.kode_bukti_siap === false}>
                {a.name} · {idr(a.balance)}{a.kode_bukti_siap === false ? " — belum siap (nomor bukti)" : ""}
              </option>
            ))}
          </select>
        </Fld>
        <Fld label="Kategori Beban" col2><select value={f.kategori_beban_id} onChange={e => setF({ ...f, kategori_beban_id: e.target.value })} className="in" data-testid="petty-exp-kat"><option value="">Pilih...</option>{bebanAccs.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Fld>
        <Fld label="PIC / penanggung jawab (wajib)" col2><input value={f.penerima} onChange={e => setF({ ...f, penerima: e.target.value })} placeholder="Nama yang bertanggung jawab atas pengeluaran ini" className="in" data-testid="petty-exp-pic" list="petty-pic-karyawan" /><datalist id="petty-pic-karyawan">{karyawan.map(n => <option key={n} value={n} />)}</datalist></Fld>
        <Fld label="Foto nota / bukti (wajib)" col2>
          <PemilihBukti h={hBukti} testid="petty-exp-bukti-file" />
        </Fld>
        <Fld label="Cabang pengeluaran" col2>
          <select value={f.cabang} onChange={e => setF({ ...f, cabang: e.target.value })} className="in" data-testid="petty-exp-cabang">
            <option value="">Ikut cabang akun kas kecil yang dipilih</option>
            {cabangOptions.map(c => (
              <option key={c} value={c} disabled={tanpaKode.includes(c)}>
                {c}{tanpaKode.includes(c) ? " (belum ada kode)" : ""}
              </option>
            ))}
          </select>
        </Fld>
        <Fld label="Keterangan" col2><textarea rows={2} value={f.keterangan} onChange={e => setF({ ...f, keterangan: e.target.value })} className="in" style={{ height: "auto", padding: "10px 12px" }} /></Fld>
      </div>

      {/* K3: nomor yang AKAN terbit, dibaca dari counter sungguhan di server. */}
      <div className={`mt-4 p-3 rounded-lg border text-xs ${pratinjau && pratinjau.error ? "border-rose-200 bg-rose-50 text-rose-700" : "border-slate-200 bg-slate-50 text-slate-700"}`}
           data-testid="petty-exp-nomor-berikutnya">
        <span className="font-semibold">Nomor berikutnya: </span>
        {!f.akun_id ? <span className="text-slate-500">pilih dulu kas kecilnya</span>
          : pratinjau && pratinjau.error ? <span>{pratinjau.error}</span>
            : pratinjau ? <span className="font-mono font-bold">{pratinjau.nomor}</span>
              : <span className="text-slate-500">menghitung...</span>}
      </div>

      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-800">📸 Foto nota wajib — unggah langsung dari sini (JPG/JPEG/PNG/PDF, maks 20 MB); tidak perlu lagi lewat menu Documents. Nama PIC juga wajib: dialah yang ditanya kalau nota ini dipertanyakan.</div>
      {can("finance_petty", "create") ? (
        <button onClick={submit} disabled={busy || unggah || belumSiap || !f.akun_id || !f.kategori_beban_id || !f.nominal || !bukti || !f.penerima.trim()} data-testid="petty-exp-submit" className="mt-4 w-full h-11 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium disabled:opacity-50">{busy ? "Menyimpan..." : "Catat Pengeluaran"}</button>
      ) : (
        <p className="mt-4 text-xs text-slate-400 italic">Akun Anda tidak punya izin mencatat pengeluaran petty cash.</p>
      )}
      {belumSiap && (
        <p className="mt-2 text-[11px] text-rose-700 flex items-start gap-1" data-testid="petty-exp-belum-siap">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {cabangBelumKode
            ? `Cabang "${f.cabang}" belum punya kode nomor bukti — minta pemegang izin Pengaturan Finance mengisinya di Pengaturan Finance › Kode Nomor Bukti Kas Kecil.`
            : (akunTerpilih && akunTerpilih.kode_bukti_masalah)}
        </p>
      )}
      <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #9333EA; }`}</style>
    </div>
  );
}

function TabReplenish({ bukaSetting }) {
  const { user, can } = useAuth();
  const [items, setItems] = useState([]);
  const [petty, setPetty] = useState([]);
  const [bank, setBank] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/finance/petty-cash/replenishment").then(r => setItems(r.data.items || []));
  useEffect(() => { load(); api.get("/finance/petty-cash/accounts").then(r => setPetty(r.data.items || [])); api.get("/finance/accounts/kas-bank").then(r => setBank((r.data || []).filter(a => a.category === "bank" || a.category === "kas"))); }, []);
  // `extra` dipakai approve untuk menitipkan bukti transfer yang baru diunggah
  // penyetuju (14c) — satu jalur kirim, bukan dua fungsi yang bisa berpisah.
  const decide = async (id, action, extra) => {
    let body = extra || {};
    if (action === "reject") { const reason = prompt("Alasan tolak?"); if (!reason) return; body = { reason }; }
    try { const r = await api.post(`/finance/petty-cash/replenishment/${id}/${action}`, body); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="space-y-4">
      {can("finance_petty", "create") && (
        <button onClick={() => setShowForm(true)} data-testid="petty-rep-new" className="h-10 px-4 rounded-lg bg-purple-600 text-white hover:bg-purple-700 text-sm font-medium inline-flex items-center gap-2"><Send className="w-4 h-4" /> Ajukan Replenishment</button>
      )}
      <div className="space-y-2">
        {items.length === 0 ? <p className="text-slate-400 text-sm">Belum ada replenishment</p> :
          items.map(r => (
            <KartuReplenish key={r.id} r={r} user={user} decide={decide} />
          ))
        }
      </div>
      <PanduanAkunBelumSiap petty={petty} bukaSetting={bukaSetting} testid="petty-rep-panduan" />
      {showForm && (
        <FormReplenish petty={petty} bank={bank} bukaSetting={bukaSetting}
          onClose={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
}
/** Satu baris request top-up. Komponen sendiri karena tiap baris butuh state
 *  unggah bukti masing-masing — hook tidak boleh dipanggil di dalam `.map`. */
function KartuReplenish({ r, user, decide }) {
  const hBukti = useUnggahBukti();
  const [lihat, setLihat] = useState(null);
  // 14c: backend menolak approve tanpa bukti transfer. Gerbang di layar dibuat
  // SAMA supaya tombolnya tidak pernah berujung 400 (CLAUDE.md #3): kalau
  // request belum membawa bukti, penyetuju harus mengunggahnya dulu di sini.
  const sudahAdaBukti = !!r.bukti_file_id;
  const buktiSiap = sudahAdaBukti || !!hBukti.bukti;
  const bolehSetujui = r.kode_bukti_siap !== false && buktiSiap;
  const alasanMati = r.kode_bukti_siap === false ? r.kode_bukti_masalah
    : (!buktiSiap ? "Lampirkan bukti transfer dulu" : undefined);
  return (
    <div className={`border rounded-xl p-4 ${r.status === "pending" ? "bg-amber-50 border-amber-200" : r.status === "approved" ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-mono text-slate-500">{r.code}</p>
          <p className="font-medium text-slate-900">{r.akun_name} ← {r.dari_akun_name}</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{idr(r.jumlah)}</p>
          <p className="text-xs text-slate-500 mt-1">Oleh {r.created_by_name}{r.catatan ? ` · ${r.catatan}` : ""}</p>
          {/* Nomor bukti PIT/PIE baru terbit saat disetujui — sebelum itu
              yang ada cuma `code` PTC-REP sebagai identitas request. */}
          {r.nomor_bukti && <p className="text-[10px] font-mono text-emerald-700 mt-1" data-testid={`petty-rep-nomor-${r.id}`}>Bukti masuk: {r.nomor_bukti}</p>}
          {sudahAdaBukti && (
            // Dibuka lewat `ModalBuktiPetty` yang SUDAH ada, bukan tautan
            // langsung: endpoint pratinjau butuh header Authorization, dan
            // `<a href>` tidak membawanya (tautannya akan selalu 401).
            <button type="button"
                    onClick={() => setLihat({ id: r.bukti_file_id, jenis: "file", original_name: r.bukti_nama })}
                    data-testid={`petty-rep-bukti-lihat-${r.id}`}
                    className="text-[11px] text-purple-700 hover:underline mt-1 inline-flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" /> Bukti transfer: {r.bukti_nama || "lihat"}
            </button>
          )}
        </div>
        {r.status === "pending" && bolehApproveRejectPetty(user) && (
          <div className="flex gap-2">
            <button onClick={() => decide(r.id, "reject")} className="h-9 px-3 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs font-medium border border-rose-200"><X className="w-3.5 h-3.5" /></button>
            <button onClick={() => decide(r.id, "approve", hBukti.bukti ? { bukti_file_id: hBukti.bukti.id } : {})}
                    disabled={!bolehSetujui} title={alasanMati}
                    data-testid={`petty-rep-appr-${r.id}`} className="h-9 px-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50"><Check className="w-3.5 h-3.5" /> Setujui & Transfer</button>
          </div>
        )}
        {r.status !== "pending" && <span className={`text-[10px] font-semibold uppercase ${r.status === "approved" ? "text-emerald-700" : "text-slate-500"}`}>{r.status}</span>}
      </div>
      {/* Request lama bisa lahir sebelum validasi dini ada; tombolnya
          dimatikan berikut alasannya, bukan dibiarkan berujung 400. */}
      {r.status === "pending" && r.kode_bukti_siap === false && (
        <p className="mt-2 text-[11px] text-rose-700 flex items-start gap-1" data-testid={`petty-rep-masalah-${r.id}`}>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {r.kode_bukti_masalah}
        </p>
      )}
      {/* Request yang diajukan sebelum aturan 14c ada (atau yang transfernya
          baru dilakukan sekarang) diselesaikan dengan melampirkan buktinya di
          sini — tidak perlu diajukan ulang. */}
      {r.status === "pending" && !sudahAdaBukti && bolehApproveRejectPetty(user) && (
        <div className="mt-3 pt-3 border-t border-amber-200">
          <p className="text-[11px] font-medium text-slate-700 mb-1">Bukti transfer (wajib sebelum disetujui)</p>
          <PemilihBukti h={hBukti} testid={`petty-rep-bukti-file-${r.id}`} />
        </div>
      )}
      <ModalBuktiPetty bukti={lihat} onClose={() => setLihat(null)} />
    </div>
  );
}

function FormReplenish({ petty, bank, onClose, bukaSetting }) {
  const [f, setF] = useState({ akun_id: "", jumlah: "", dari_akun_id: "", catatan: "" });
  const [busy, setBusy] = useState(false);
  // 14c: bukti transfer BOLEH dilampirkan sekarang, tapi tidak wajib — saat
  // mengajukan, transfernya sering belum terjadi. Yang wajib adalah saat
  // menyetujui (dijaga backend).
  const hBukti = useUnggahBukti();
  // Akun tujuan yang belum berkode SENGAJA tidak ikut jadi pilihan otomatis:
  // request-nya akan ditolak 400 sejak pengajuan (validasi dini K3).
  useEffect(() => { const target = petty.find(p => p.gap_replenish > 0 && p.kode_bukti_siap !== false); if (target) setF(x => ({ ...x, akun_id: target.id, jumlah: target.gap_replenish, dari_akun_id: target.replenishment_source_akun_id || "" })); }, [petty]);
  const target = petty.find(p => p.id === f.akun_id);
  const targetBelumSiap = !!target && target.kode_bukti_siap === false;
  const submit = async () => {
    setBusy(true);
    try { const r = await api.post("/finance/petty-cash/replenishment", { ...f, jumlah: Number(f.jumlah), bukti_file_id: hBukti.bukti ? hBukti.bukti.id : null }); toast.success(r.data.message); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  // `dangerous` — jumlah replenishment diketik/dipilih staf sendiri, plus
  // pilihan akun tujuan & sumber dana.
  const guard = useModalGuard({ onClose, onSubmit: submit, busy, disabled: busy || targetBelumSiap || !f.akun_id || !f.jumlah || !f.dari_akun_id, dangerous: true });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid="petty-rep-modal" className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-4">Ajukan Replenishment</h3>
        <div className="space-y-3">
          <PanduanAkunBelumSiap petty={petty} bukaSetting={bukaSetting} testid="petty-rep-modal-panduan" />
          <Fld label="Petty Cash Tujuan">
            <select value={f.akun_id} onChange={e => setF({ ...f, akun_id: e.target.value })} className="in" data-testid="petty-rep-target">
              <option value="">Pilih...</option>
              {petty.map(a => (
                <option key={a.id} value={a.id} disabled={a.kode_bukti_siap === false}>
                  {a.name} · saldo {idr(a.balance)} (gap {idr(a.gap_replenish || 0)}){a.kode_bukti_siap === false ? " — belum siap (nomor bukti)" : ""}
                </option>
              ))}
            </select>
          </Fld>
          <Fld label="Jumlah"><InputRupiah value={f.jumlah} onChange={n => setF({ ...f, jumlah: n })} className="in" testid="petty-rep-jumlah" /></Fld>
          <Fld label="Bukti transfer (boleh menyusul saat disetujui)">
            <PemilihBukti h={hBukti} testid="petty-rep-bukti-file" />
            <p className="text-[11px] text-slate-500 mt-1">
              Kalau transfernya belum dilakukan, kosongkan saja — penyetuju wajib
              melampirkannya sebelum top-up masuk buku.
            </p>
          </Fld>
          <Fld label="Sumber Dana (Bank/Kas)"><select value={f.dari_akun_id} onChange={e => setF({ ...f, dari_akun_id: e.target.value })} className="in" data-testid="petty-rep-source"><option value="">Pilih...</option>{bank.map(a => <option key={a.id} value={a.id}>{a.name} · {idr(a.balance)}</option>)}</select></Fld>
          <Fld label="Catatan"><input value={f.catatan} onChange={e => setF({ ...f, catatan: e.target.value })} className="in" /></Fld>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">💡 Request akan pending sampai SPV approve. Setelah approve, dana otomatis transfer dari Bank ke Petty Cash dan nomor bukti masuk (PIT/PIE) terbit.</p>
        {targetBelumSiap && (
          <p className="mt-2 text-[11px] text-rose-700 flex items-start gap-1" data-testid="petty-rep-belum-siap">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {target.kode_bukti_masalah}
          </p>
        )}
        {guard.confirming && (
          <div className="mt-3 -mx-6"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="petty-rep-guard" /></div>
        )}
        <div className="flex gap-2 mt-5"><button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Batal</button><button onClick={submit} disabled={busy || targetBelumSiap || !f.akun_id || !f.jumlah || !f.dari_akun_id} data-testid="petty-rep-submit" className="flex-1 h-10 rounded-lg bg-purple-600 text-white hover:bg-purple-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Kirim"}</button></div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #9333EA; }`}</style>
      </div>
    </div>
  );
}

// ===========================================================================
// K5 & K8 (brief owner 4 Sep 2026) — riwayat per akun + export Excel.
// Komponen di bawah ini SELURUHNYA milik gugus K5/K8 dan sengaja diletakkan di
// ujung berkas supaya tidak bertabrakan dengan form/tab di atasnya.
// ===========================================================================

/** Panduan yang muncul saat TIDAK SATU PUN akun kas kecil bisa dipilih.
 *
 * G3/G4, brief owner 15 Sep 2026. Owner melaporkan dua dropdown kas kecil
 * "tidak dapat ditekan" dan menduga itu soal izin — dia sampai meminta akses.
 * Yang sebenarnya terjadi: keempat akun kas kecil produksi `jenis`-nya
 * (Tunai/E-Wallet) belum disetel, sehingga `resolusi_kode_bukti` menolak
 * menerbitkan nomor bukti dan SETIAP `<option>` lahir `disabled`.
 *
 * Penjelasannya sebetulnya sudah ada di layar sejak K3 (4 Sep) — tapi
 * digerbangi `akunTerpilih`, yaitu SETELAH sebuah akun terpilih. Pada keadaan
 * ini memilih akun justru mustahil, jadi pesan itu tidak pernah terbaca
 * siapa pun. Itu bentuk lain dari jebakan yang sudah tercatat di CLAUDE.md:
 * "gejala 'selalu ditolak dengan pesan yang masuk akal' lebih mudah disangka
 * salah pakai daripada dicurigai sebagai bug" — di sini bahkan pesannya pun
 * tidak sempat muncul.
 *
 * Sengaja HANYA muncul saat nol akun bisa dipilih. Kalau masih ada satu yang
 * siap, pengguna punya jalan maju dan penanda per-baris di dropdown sudah
 * cukup; memunculkan spanduk di situ cuma melatih orang mengabaikannya.
 *
 * Tombol "Setel Jenis Kas" digerbangi izin yang SAMA dengan endpoint
 * `PUT /finance/petty-cash/accounts/{id}` (`finance_petty.update`) — aturan #3
 * CLAUDE.md. Yang tidak punya izin tetap melihat SEBABNYA, plus kalimat siapa
 * yang harus diminta; itu yang membedakan "mentok tanpa tahu kenapa" dari
 * "tahu persis apa yang harus diminta ke siapa".
 */
function PanduanAkunBelumSiap({ petty, bukaSetting, testid }) {
  const { can } = useAuth();
  const akunList = petty || [];
  // `=== false` (bukan `!`) supaya respons server LAMA yang belum mengirim
  // medan ini berperilaku persis seperti dulu — pola yang sama dengan
  // `BadgeJenisKas` di bawah.
  const terblokir = akunList.filter(a => a.kode_bukti_siap === false);
  const adaYangSiap = akunList.some(a => a.kode_bukti_siap !== false);
  if (akunList.length === 0 || adaYangSiap || terblokir.length === 0) return null;
  const bolehSetel = can("finance_petty", "update");
  return (
    <div data-testid={testid}
      className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
      <p className="font-semibold flex items-start gap-1.5">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
        Semua kas kecil belum bisa dipakai — ini BUKAN soal izin akun Anda.
      </p>
      <ul className="mt-2 space-y-1.5">
        {terblokir.map(a => (
          <li key={a.id} className="flex flex-wrap items-center gap-x-2 gap-y-1"
            data-testid={`${testid}-akun-${a.code}`}>
            <span className="font-semibold">{a.name}</span>
            <span className="text-amber-700">{a.kode_bukti_masalah}</span>
            {bolehSetel && bukaSetting && (
              <button onClick={() => bukaSetting(a.id)}
                data-testid={`${testid}-setel-${a.code}`}
                className="inline-flex items-center gap-1 h-7 px-2 rounded border border-amber-400 bg-white hover:bg-amber-100 font-medium">
                <Settings className="w-3 h-3" /> Setel Jenis Kas <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-amber-700">
        {bolehSetel
          ? "Nomor bukti dirangkai dari jenis kas (Tunai = PCT/PIT, E-Wallet = PCE/PIE), jadi tidak bisa ditebak selama jenisnya kosong. Setel sekali, lalu dropdown ini hidup."
          : "Minta pemegang izin Kas Kecil (SPV/CEO Finance) membuka Kas Kecil › Overview › tombol Setting di kartu akun, lalu memilih Jenis Kas (Tunai/E-Wallet). Sekali disetel, dropdown ini langsung hidup."}
      </p>
    </div>
  );
}

/** Badge jenis kas + penanda "nomor bukti belum siap".
 *
 * Akun yang belum menyetel jenisnya TIDAK disembunyikan (datanya tetap harus
 * terlihat), tapi ditandai apa adanya — backend menolak menerbitkan nomor bukti
 * untuk akun seperti itu, dan di sinilah orang tahu kenapa. `jenis_disetel` /
 * `kode_bukti_siap` diperiksa dengan `=== false` supaya respons server lama
 * (yang belum mengirim field ini) berperilaku persis seperti dulu.
 */
function BadgeJenisKas({ akun }) {
  const ewallet = akun.jenis === "ewallet";
  const IkonJenis = ewallet ? Smartphone : Wallet;
  const belumDisetel = akun.jenis_disetel === false;
  const belumSiap = akun.kode_bukti_siap === false;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <span
        data-testid={`petty-jenis-${akun.code}`}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${ewallet ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
      >
        <IkonJenis className="w-3 h-3" /> {ewallet ? "E-Wallet" : "Tunai"}
        {belumDisetel && <span className="font-normal italic">(belum disetel)</span>}
      </span>
      {belumSiap && (
        <span
          data-testid={`petty-kode-belum-siap-${akun.code}`}
          title={akun.kode_bukti_masalah || ""}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-amber-50 text-amber-800 border-amber-200"
        >
          <AlertTriangle className="w-3 h-3" /> Nomor bukti belum siap
        </span>
      )}
      {belumSiap && akun.kode_bukti_masalah && (
        <span className="basis-full text-[10px] leading-snug text-amber-700">{akun.kode_bukti_masalah}</span>
      )}
    </div>
  );
}

/** Modal pratinjau bukti nota.
 *
 * Berkas diambil lewat axios (`responseType: "blob"`) supaya JWT-nya ikut di
 * HEADER, bukan ditempel di URL sebagai `?auth=` — URL bocor ke riwayat browser,
 * log proxy, dan tombol "salin alamat gambar". Bukti LAMA yang cuma berupa URL
 * (data sebelum K4) dibuka apa adanya sebagai tautan: isinya tidak kita simpan,
 * jadi tidak ada yang bisa dijanjikan soal pratinjaunya.
 */
function ModalBuktiPetty({ bukti, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [gagal, setGagal] = useState(null);
  useEffect(() => {
    if (!bukti || bukti.jenis !== "file") return undefined;
    let batal = false;
    let dibuat = null;
    setGagal(null);
    setBlobUrl(null);
    api.get(`/finance/petty-cash/bukti/${bukti.id}/preview`, { responseType: "blob" })
      .then(r => {
        if (batal) return;
        dibuat = URL.createObjectURL(r.data);
        setBlobUrl(dibuat);
      })
      .catch(() => { if (!batal) setGagal("Bukti tidak bisa dimuat — berkasnya hilang, atau akun Anda tidak boleh membukanya."); });
    return () => { batal = true; if (dibuat) URL.revokeObjectURL(dibuat); };
  }, [bukti]);
  if (!bukti) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="petty-bukti-modal" className="bg-white rounded-2xl max-w-2xl w-full p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg">Bukti Nota</h3>
          <button onClick={onClose} data-testid="petty-bukti-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {gagal && <p className="text-sm text-rose-700">{gagal}</p>}
        {!gagal && !blobUrl && <p className="text-sm text-slate-500">Memuat bukti...</p>}
        {blobUrl && (
          <>
            <img src={blobUrl} alt="Bukti nota" className="max-h-[65vh] w-full object-contain rounded-lg border border-slate-200" />
            <p className="mt-2 text-[11px] text-slate-500">Bukti PDF ditampilkan sebagai gambar halaman pertama.</p>
          </>
        )}
      </div>
    </div>
  );
}

/** Panel riwayat mutasi satu akun kas kecil (K5).
 *
 * Filter tanggal dikirim ke SERVER, bukan disaring di layar: daftarnya dipotong
 * di server, jadi penyaringan sisi-klien akan melewatkan baris di luar potongan
 * itu dan melapor "tidak ada" dengan yakin (CLAUDE.md). Totalnya datang dari
 * agregasi server, jadi tetap benar walau daftarnya terpotong.
 */
function PanelRiwayatKasKecil({ akun, onClose }) {
  const [data, setData] = useState(null);
  const [memuat, setMemuat] = useState(true);
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [terpakai, setTerpakai] = useState({ dari: "", sampai: "" });
  const [bukti, setBukti] = useState(null);
  useEffect(() => {
    let batal = false;
    setMemuat(true);
    const params = {};
    if (terpakai.dari) params.date_from = terpakai.dari;
    if (terpakai.sampai) params.date_to = terpakai.sampai;
    api.get(`/finance/petty-cash/accounts/${akun.id}/riwayat`, { params })
      .then(r => { if (!batal) setData(r.data); })
      .catch(e => { if (!batal) { setData(null); toast.error(e?.response?.data?.detail || "Gagal memuat riwayat"); } })
      .finally(() => { if (!batal) setMemuat(false); });
    return () => { batal = true; };
  }, [akun.id, terpakai]);

  const tampilkan = () => {
    if (dari && sampai && dari > sampai) return toast.error("Tanggal 'dari' melewati tanggal 'sampai'");
    setTerpakai({ dari, sampai });
  };
  const reset = () => { setDari(""); setSampai(""); setTerpakai({ dari: "", sampai: "" }); };
  const items = data?.items || [];

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="petty-riwayat-panel">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Riwayat Transaksi Kas Kecil</p>
          <p className="font-display font-bold text-lg text-slate-900">{akun.code} · {akun.name}</p>
          <p className="text-sm text-slate-600 mt-0.5">Saldo: <b>{idr(akun.balance)}</b></p>
        </div>
        <button onClick={onClose} data-testid="petty-riwayat-close" className="text-sm text-slate-500 hover:text-slate-900">Tutup</button>
      </div>
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Dari Tanggal</label>
          <input type="date" value={dari} onChange={e => setDari(e.target.value)} data-testid="petty-riwayat-dari" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Sampai Tanggal</label>
          <input type="date" value={sampai} onChange={e => setSampai(e.target.value)} data-testid="petty-riwayat-sampai" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <button onClick={tampilkan} data-testid="petty-riwayat-cari" className="h-9 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">Tampilkan</button>
        {(terpakai.dari || terpakai.sampai) && (
          <button onClick={reset} data-testid="petty-riwayat-reset" className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm">Reset</button>
        )}
        {data && (
          <div className="ml-auto text-right text-xs">
            <p className="text-emerald-700 font-semibold" data-testid="petty-riwayat-total-masuk">Masuk: {idr(data.total_masuk || 0)}</p>
            <p className="text-rose-700 font-semibold" data-testid="petty-riwayat-total-keluar">Keluar: {idr(data.total_keluar || 0)}</p>
            <p className="text-slate-400">{data.jumlah_baris || 0} baris di periode ini</p>
          </div>
        )}
      </div>
      {data?.terpotong && (
        <div className="px-5 pt-3">
          {/* Totalnya TETAP benar walau daftarnya terpotong — dihitung agregasi di
              server, bukan dijumlah dari baris yang kebetulan tampil. */}
          <BatasDaftarNotice shown={items.length} total={data.jumlah_baris} testid="petty-riwayat-batas" />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              {["Kode", "Tanggal", "Aktivitas", "Kategori Beban", "Cabang", "Nominal", "PIC", "Bukti"].map(h => (
                <th key={h} className="text-left font-semibold px-4 py-2 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {memuat && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">Memuat...</td></tr>}
            {!memuat && items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">Belum ada transaksi di periode ini</td></tr>
            )}
            {!memuat && items.map((b, i) => (
              <tr key={`${b.transaction_id}-${i}`} data-testid={`petty-riwayat-row-${i}`} className={b.dibatalkan ? "line-through text-slate-400" : ""}>
                <td className="px-4 py-2 font-mono text-[11px] whitespace-nowrap">{b.kode || "-"}</td>
                <td className="px-4 py-2 whitespace-nowrap">{formatTanggal(b.tanggal)}</td>
                <td className="px-4 py-2">
                  {b.aktivitas}
                  {(b.perhatian || []).length > 0 && (
                    <span title={(b.perhatian || []).join(" · ")} data-testid={`petty-riwayat-perhatian-${i}`} className="ml-1 inline-flex align-middle text-amber-600">
                      <AlertTriangle className="w-3.5 h-3.5" />
                    </span>
                  )}
                  {b.keterangan && <span className="block text-[11px] text-slate-400">{b.keterangan}</span>}
                </td>
                <td className="px-4 py-2">{b.kategori_beban || "-"}</td>
                <td className="px-4 py-2">{b.cabang || "-"}</td>
                <td className={`px-4 py-2 text-right whitespace-nowrap font-medium ${b.arah === "masuk" ? "text-emerald-700" : "text-rose-700"}`}>
                  {b.arah === "masuk" ? "+" : "−"} {idr(b.nominal || 0)}
                </td>
                <td className="px-4 py-2">{b.pic || "-"}</td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {b.bukti?.jenis === "file" && (
                    <button onClick={() => setBukti(b.bukti)} data-testid={`petty-riwayat-bukti-${i}`} className="inline-flex items-center gap-1 text-purple-700 hover:underline text-xs font-medium">
                      <Eye className="w-3.5 h-3.5" /> Lihat bukti
                    </button>
                  )}
                  {b.bukti?.jenis === "url" && (
                    <a href={b.bukti.url} target="_blank" rel="noreferrer" data-testid={`petty-riwayat-bukti-url-${i}`} className="text-slate-600 hover:underline text-xs">Buka URL</a>
                  )}
                  {!b.bukti?.jenis && <span className="text-slate-300">-</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ModalBuktiPetty bukti={bukti} onClose={() => setBukti(null)} />
    </div>
  );
}

// "Pusat / tanpa cabang" dikirim sebagai parameter `tanpa_cabang=1` yang
// TERPISAH — bukan sentinel `cabang=pusat`. "Pusat" bisa jadi nama cabang
// sungguhan di data, dan sentinel seperti itu akan menelan datanya diam-diam.
const OPSI_TANPA_CABANG = "__tanpa_cabang__";

/** Tombol + pemilih filter export Excel kas kecil (K8). */
function ExportKasKecil({ cabangOptions }) {
  const [bulan, setBulan] = useState(bulanIniISO());
  const [cabang, setCabang] = useState("");
  const [jenis, setJenis] = useState("");
  const [sibuk, setSibuk] = useState(false);
  const unduh = async () => {
    if (!/^\d{4}-\d{2}$/.test(bulan)) return toast.error("Pilih bulan dulu ya 😊");
    setSibuk(true);
    try {
      const params = { bulan };
      if (cabang === OPSI_TANPA_CABANG) params.tanpa_cabang = 1;
      else if (cabang) params.cabang = cabang;
      if (jenis) params.jenis = jenis;
      const res = await api.get("/finance/petty-cash/export-xlsx", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Kas-Kecil-${bulan}${jenis ? "-" + jenis : ""}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Rekap kas kecil terunduh ✓");
    } catch (e) {
      // Dengan `responseType: "blob"`, badan error 400 dari server ikut jadi Blob —
      // membacanya apa adanya memberi "[object Blob]" alih-alih alasan penolakannya.
      let pesan = "Gagal mengunduh rekap";
      try {
        const isi = e?.response?.data;
        if (isi instanceof Blob) pesan = JSON.parse(await isi.text())?.detail || pesan;
        else pesan = isi?.detail || pesan;
      } catch { /* biarkan pesan bawaan */ }
      toast.error(pesan);
    } finally { setSibuk(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-wrap items-end gap-3">
      <div>
        <label className="block text-[11px] font-medium text-slate-500 mb-1">Bulan</label>
        <input type="month" value={bulan} onChange={e => setBulan(e.target.value)} data-testid="petty-export-bulan" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
      </div>
      <div>
        <label className="block text-[11px] font-medium text-slate-500 mb-1">Cabang</label>
        <select value={cabang} onChange={e => setCabang(e.target.value)} data-testid="petty-export-cabang" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" style={{ minWidth: 190 }}>
          <option value="">Semua cabang</option>
          <option value={OPSI_TANPA_CABANG}>Pusat / tanpa cabang</option>
          {cabangOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[11px] font-medium text-slate-500 mb-1">Jenis Kas</label>
        <select value={jenis} onChange={e => setJenis(e.target.value)} data-testid="petty-export-jenis" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" style={{ minWidth: 160 }}>
          <option value="">Tunai + E-Wallet</option>
          <option value="tunai">Tunai saja</option>
          <option value="ewallet">E-Wallet saja</option>
        </select>
      </div>
      <button onClick={unduh} disabled={sibuk} data-testid="petty-export-xlsx" className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
        <FileSpreadsheet className="w-4 h-4" /> {sibuk ? "Menyiapkan..." : "Export Excel"}
      </button>
      <p className="basis-full text-[11px] text-slate-500">
        Berkasnya berisi tiga sheet: Ringkasan, Tunai, dan E-Wallet — pengeluaran, top-up, dan aktivitas lain terpisah dengan subtotalnya.
      </p>
    </div>
  );
}
